import {
  QrCode,
  coupleGuide,
  singleGuide,
  verifyEcgSignalIcon,
  electrodesPlacementIcon,
  laIcon,
  llIcon,
  openIcon,
  raIcon,
} from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import { isWebViewCreateStudy } from '@/shared/lib';
import { forwardRef } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useNewStudyStep5 } from './useNewStudyStep5';
import type { NewStudyStep5Props } from './newStudyStep5.types';
import './newStudyStep5.scss';

/**
 * Final step of the create-study wizard, giving post-creation patient instructions once the study exists. Renders the optional return shipping label block, then branches on props.isUsingStudyBiotresList: the Biotres variant shows Biocare Health app download copy that differs for US and non-US patients plus a QR code and two electrode placement guides, while the default variant shows the ECG signal verification guidance with the electrode placement images and per-lead placement notes.
 *
 * @returns Step 5 content element for the selected device flow.
 */
const NewStudyStep5 = forwardRef((props: NewStudyStep5Props, ref) => {
  const { onClickCreateReturnShipping } = useNewStudyStep5(props, ref);

  const isWebView = isWebViewCreateStudy();

  /**
   * Builds the return shipping label call-to-action shown on the final create-study step. Returns null in the create-study web view, for a facility with Test status and for a test study, otherwise renders the explanatory copy and a button wired to onClickCreateReturnShipping for creating the patient's return label.
   *
   * @returns Return shipping label block, or null when it must be hidden.
   */
  const renderShippingLabel = () => {
    if (isWebView) {
      return null;
    }

    if (props.defaultData?.study?.facility?.status === 'Test') {
      return null;
    }

    if (props.defaultData?.study?.isTest) {
      return null;
    }

    return (
      <div className="return-shipping-label">
        <div className="return-shipping-label__title">{t`Return shipping label`}</div>
        <div className="return-shipping-label__description">
          {t`Create a return shipping label for the patient to use when sending back the device after the study is completed. You can also create it later on the Study Management page.`}
        </div>
        <CustomButton
          id="create-return-shipping-label-btn"
          onClick={onClickCreateReturnShipping}
          style="default"
          type="outline"
          icon={<ReactSVG src={openIcon} />}
          isRightIcon
          size="small"
          className="return-shipping-label__btn"
        >
          {t`Create return shipping label`}
        </CustomButton>
      </div>
    );
  };

  return props.isUsingStudyBiotresList ? (
    <div className="step-content step-5 part-1">
      {renderShippingLabel()}
      <div className="centered-content w-100">
        <div className="infor-message">
          <div className="text">
            <div className="header">{t`Get Biocare Health`}</div>
            <span className="content">
              {props.selectedCountry === 'US'
                ? t`A link to download Biocare Health app and a reference code will be sent to the patient’s primary phone number (and email if any). Alternatively, the patient can download it directly using the QR code provided here.
                  You can resend them from the Study details page.`
                : t`A link to download Biocare Health app and a reference code will be sent to the patient’s email. You can resend them from the Study details page.`}
            </span>
          </div>
          <div className="qr-code">
            <img src={QrCode} alt="QR Code" />
          </div>
        </div>
        <div className="warn-message">
          <div className="header">{t`Verify ECG signal`}</div>
          <span className="content">
            {t`Please check evaluation strips after starting the study to ensure that the ECG signal is identifiable.`}
          </span>
        </div>
        <div className="list-data">
          <div className="list-item">
            <div className="bgGuide">
              <img src={coupleGuide} alt="coupleGuide" />
            </div>
            <div className="item-content">
              <div className="title">Unified male and female application</div>
              <div className="info-item">
                <img src={raIcon} alt="raIcon" className="icon-webview" />
                {t`Upper-right portion of the chest, 2 to 3 inches, below collarbone`}
              </div>
              <div className="info-item">
                <img src={llIcon} alt="" aria-hidden="true" className="icon-webview" />
                {t`Between breasts, vertically aligned with the umbilicus.`}
              </div>
              <div className="info-item">
                <img src={laIcon} alt="" aria-hidden="true" className="icon-webview" />
                {t`Upper-left portion of the chest, 2 to 3 inches, below collarbone`}
              </div>
            </div>
          </div>
        </div>
        <div className="list-data">
          <div className="list-item">
            <div className="bgGuide">
              <img src={singleGuide} alt="singleGuide" />
            </div>
            <div className="item-content">
              <div className="title">Alternative placement option</div>
              <div className="info-item">
                <img src={raIcon} alt="raIcon" className="icon-webview" />
                {t`2 to 3 inches below the left sternum and collarbone junction.`}
              </div>
              <div className="info-item">
                <img src={llIcon} alt="" aria-hidden="true" className="icon-webview" />
                {t`Face downward, perpendicular to ground.`}
              </div>
              <div className="info-item">
                <img src={laIcon} alt="" aria-hidden="true" className="icon-webview" />
                {t`2 to 3 inches below left collarbone.`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="step-content step-5">
      {renderShippingLabel()}
      <div className="centered-content w-100">
        <div className="warn-message">
          <div className="header">{t`Verify ECG signal`}</div>
          <div className="content">
            <div>
              {t`Please ensure that an identifiable ECG signal is shown on the device before starting a study. Should the ECG signal be unstable in all three channels, be certain that the cable wires are not loose and all contact points are correctly positioned. If you have attempted all the measures above and do not have at least 1 stable channel, it may be necessary to replace the cable.`}
            </div>
            <div>
              <strong>
                {t`REMEMBER: 1 clear channel is adequate for a study. Channels naturally go in and out based on individuals and motion.`}
              </strong>
            </div>
          </div>
        </div>
        <div className="verify-ecg-signal-container">
          <div className="electrodes-placement">
            <img
              className="electrodes-placement__img"
              src={electrodesPlacementIcon}
              alt="Electrodes placement"
            />
          </div>
          <div className="right-img">
            <img src={verifyEcgSignalIcon} alt="verifyEcgSignalIcon" />
          </div>
        </div>
        <div className="electrodes-placement__info">
          <div className="info-item">
            <img src={raIcon} alt="raIcon" className="icon-webview" />
            {t`Upper-right portion of the chest, 2 to 3 inches, below collarbone.`}
          </div>
          <div className="info-item">
            <img src={laIcon} alt="" aria-hidden="true" className="icon-webview" />
            {t`Upper-left portion of the chest, 2 to 3 inches, below collarbone.`}
          </div>
          <div className="info-item">
            <img src={llIcon} alt="" aria-hidden="true" className="icon-webview" />
            {t`Below-left breast, over the lower ribcage.`}
          </div>
        </div>
      </div>
    </div>
  );
});

export default NewStudyStep5;
